import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { EnvioMasivoService } from 'src/app/services/transacciones/envio-masivo.service';
import { LanguageApp } from '../../laboratorio/laboratorios/laboratorios.component';

const MAX_DOCUMENTOS = 250;

@Component({
  selector: 'app-envio-masivo',
  templateUrl: './envio-masivo.component.html',
  styleUrls: ['./envio-masivo.component.scss'],
  standalone: false
})
export class EnvioMasivoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;
  @ViewChild('detalleModal', { static: false }) detalleModal?: TemplateRef<any>;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  isDtInitialized = false;

  // Filtros
  public fechaInicio: string = '';
  public fechaFin: string = '';
  public horaInicial: string = '00:00';
  public horaFinal: string = '23:59';

  // Resumen agrupado por fecha
  public resumen: any[] = [];
  public totalFacturas = 0;
  public totalSaldo = 0;
  public cargando = false;
  public busquedaRealizada = false;

  // Detalle (modal)
  public detalleModalRef?: NgbModalRef;
  public fechaSeleccionada: string = '';
  public detalle: any[] = [];
  public cargandoDetalle = false;

  // Progreso de envío
  public procesando = false;
  public total = 0;
  public procesados = 0;
  public exitosos = 0;
  public fallidos = 0;
  public porcentaje = 0;

  get token(): string { return localStorage.getItem('token') || ''; }

  constructor(
    private envioMasivoService: EnvioMasivoService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // Por defecto se consultan los pendientes de todo el año 2026
    this.fechaInicio = '2026-01-01';
    this.fechaFin = '2026-12-31';
    this.buildDtOptions();
    this.buscarResumen();
  }

  ngAfterViewInit(): void {
    // El trigger inicial se dispara cuando llegan los datos (buscarResumen)
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  private buildDtOptions(): void {
    this.dtOptions = {
      language: LanguageApp.spanish_datatables,
      pagingType: 'full_numbers',
      pageLength: 10,
      // El modo tarjeta en móvil lo controla el CSS (.mobile-friendly-table),
      // por eso desactivamos el "responsive" propio de DataTables para que no
      // colapse columnas y entren en conflicto.
      responsive: false,
      orderClasses: false,
      order: [],
    };
  }

  private get rango(): { fechaInicio: string; fechaFin: string } | null {
    if (!this.fechaInicio || !this.fechaFin || !this.horaInicial || !this.horaFinal) {
      return null;
    }
    return {
      fechaInicio: `${this.fechaInicio} ${this.horaInicial}`,
      fechaFin:    `${this.fechaFin} ${this.horaFinal}`,
    };
  }

  buscarResumen(): void {
    const rango = this.rango;
    if (!rango) {
      this.toastr.info('Por favor, seleccione las dos fechas y horas');
      return;
    }

    this.cargando = true;

    this.envioMasivoService.listarPendientesResumen(this.token, rango).subscribe({
      next: (resp: any) => {
        this.ngZone.run(() => {
          this.cargando = false;
          this.busquedaRealizada = true;
          this.resumen = Array.isArray(resp?.data) ? resp.data : [];
          this.totalFacturas = resp?.totalFacturas || 0;
          this.totalSaldo = resp?.totalSaldo || 0;
          this.rerenderTabla();

          if (this.totalFacturas === 0) {
            this.toastr.info('No hay documentos pendientes en este rango');
          }
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.cargando = false;
          this.busquedaRealizada = true;
          this.resumen = [];
          this.totalFacturas = 0;
          this.totalSaldo = 0;
          this.rerenderTabla();
          this.toastr.error('Error al consultar pendientes: ' + (err?.error?.error || 'desconocido'));
          console.error(err);
        });
      }
    });
  }

  private rerenderTabla(): void {
    if (this.isDtInitialized && this.dtElement?.dtInstance) {
      this.dtElement.dtInstance.then((dt: any) => {
        dt.destroy();
        setTimeout(() => this.dtTrigger.next(null));
      });
    } else {
      this.isDtInitialized = true;
      setTimeout(() => this.dtTrigger.next(null));
    }
  }

  /* ----------------------------- Detalle (modal) ---------------------------- */

  abrirDetalle(fecha: string): void {
    this.fechaSeleccionada = fecha;
    this.detalle = [];
    this.cargandoDetalle = true;
    this.resetProgreso();

    this.detalleModalRef = this.modalService.open(this.detalleModal, {
      size: 'xl',
      centered: true,
      scrollable: true,
      backdrop: 'static',
      windowClass: 'envio-masivo-modal',
    });

    this.envioMasivoService.listarPendientes(this.token, {
      fechaInicio: `${fecha} 00:00:00`,
      fechaFin:    `${fecha} 23:59:59`,
    }).subscribe({
      next: (resp: any) => {
        this.cargandoDetalle = false;
        this.detalle = Array.isArray(resp?.data) ? resp.data : [];
        this.total = this.detalle.length;
      },
      error: (err) => {
        this.cargandoDetalle = false;
        this.detalle = [];
        this.toastr.error('Error al cargar el detalle: ' + (err?.error?.error || 'desconocido'));
        console.error(err);
      }
    });
  }

  /* --------------------------- Envío masivo al SRI -------------------------- */

  async procesar(): Promise<void> {
    if (this.procesando) return;

    if (this.detalle.length === 0) {
      this.toastr.info('No hay documentos pendientes para procesar.');
      return;
    }

    if (this.detalle.length > MAX_DOCUMENTOS) {
      this.toastr.error(`Máximo soportado ${MAX_DOCUMENTOS} documentos por proceso`);
      return;
    }

    this.procesando = true;
    this.resetProgreso();
    this.total = this.detalle.length;

    for (const doc of this.detalle) {
      try {
        const respuesta = await lastValueFrom(
          this.envioMasivoService.enviarSRI(
            this.token,
            doc.claveFactura,
            doc.EMA_CLI,
            doc.nombre_cliente?.trim() ? doc.nombre_cliente : doc.NOM_CLI
          )
        );

        const texto = (respuesta || '').toString().toLowerCase();
        const hayError = texto.includes('error') || texto.includes('fault') || texto.includes('timed out');

        if (hayError) {
          this.fallidos++;
          doc._resultado = 'error';
          doc._mensaje = (respuesta || '').toString();
        } else {
          this.exitosos++;
          doc._resultado = 'ok';
          doc._mensaje = 'Autorizado';
        }
      } catch (err: any) {
        this.fallidos++;
        doc._resultado = 'error';
        doc._mensaje = err?.error || err?.message || 'Error de comunicación';
        console.error('[envio-masivo]', doc.claveFactura, err);
      }

      this.procesados++;
      this.porcentaje = Math.round((this.procesados * 100) / this.total);
    }

    this.procesando = false;

    if (this.fallidos === 0) {
      this.toastr.success('¡Todos los documentos se han procesado correctamente!');
    } else {
      this.toastr.warning(`Proceso finalizado: ${this.exitosos} enviados, ${this.fallidos} con error`);
    }

    // Refrescar el resumen para reflejar lo que ya salió de pendientes
    this.buscarResumen();
  }

  private resetProgreso(): void {
    this.total = this.detalle.length;
    this.procesados = 0;
    this.exitosos = 0;
    this.fallidos = 0;
    this.porcentaje = 0;
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    try {
      const d   = new Date(fecha);
      const TZ  = 'America/Guayaquil';
      const dia = d.toLocaleDateString('en-CA', { timeZone: TZ }).split('-').reverse().join('/');
      const hora = d.toLocaleTimeString('en-GB', { timeZone: TZ, hour12: false }).slice(0, 5);
      return `${dia} ${hora}`;
    } catch { return fecha; }
  }

  formatFechaCorta(fecha: string): string {
    if (!fecha) return '';
    // fecha viene como 'YYYY-MM-DD'
    const partes = fecha.split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
  }
}
